import './assets/main.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'

import App from './App.vue'
import router from './router'
import { initializeSentryMonitoring } from './utils/sentryMonitoring'

const app = createApp(App as any)

// Initialize Sentry before mounting the app
Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN || 'https://7ca2b59091b649aa8df7299e76a2b36e@o294883.ingest.us.sentry.io/1542535',
  integrations: [
    Sentry.browserTracingIntegration({
      router,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],

  // Capture errors in different environments
  environment: import.meta.env.MODE || 'development',

  // Set up performance monitoring
  // Lower sample rates for shared lessons to avoid rate limiting
  tracesSampleRate: import.meta.env.PROD ? 0.05 : 1.0, // 5% in production, 100% in dev

  // Session replay for debugging user sessions
  replaysSessionSampleRate: 0.05, // 5% of sessions
  replaysOnErrorSampleRate: 0.5, // 50% of sessions with errors (was 100%)

  // Track network requests and failures
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/api\.optimumfleethealth\.com/,
    /^https:\/\/vrx1-1\.uc\.r\.appspot\.com/,
  ],

  // Send user information for better debugging
  sendDefaultPii: true,

  // Automatically capture console logs as breadcrumbs
  beforeBreadcrumb(breadcrumb, hint) {
    // Enhance console breadcrumbs with more context
    if (breadcrumb.category === 'console') {
      return breadcrumb
    }
    return breadcrumb
  },

  // Add custom context before sending errors
  beforeSend(event, hint) {
    // IMPORTANT: Only send Sentry events for shared lesson pages to avoid rate limiting
    // This filters out events from the rest of the application
    const lessonToken = window.location.pathname.match(/\/shared\/lesson\/([^/]+)/)?.[1]

    // If not on a shared lesson page, don't send the event
    if (!lessonToken) {
      return null // Returning null drops the event
    }

    // Add lesson-specific context
    event.tags = {
      ...event.tags,
      lesson_token: lessonToken,
      feature: 'shared_lesson',
    }

    // Detect possible content filtering/blocking
    const error = hint.originalException as Error
    if (error?.message?.includes('Network Error') || error?.message?.includes('Failed to fetch')) {
      event.tags = {
        ...event.tags,
        possible_content_filter: 'true',
        error_category: 'network_failure',
      }
    }

    return event
  },
})

// Initialize enhanced Sentry monitoring (resource loading, CSP, downloads)
initializeSentryMonitoring()

const pinia = createPinia()
app.use(pinia)
app.use(router)
app.mount('#app')
